/**
 * EditStoreDialog — Radix Dialog for editing a storefront's name and subdomain.
 *
 * Label edits are always allowed. Subdomain edits are blocked for the primary
 * store (its key is the provisioned tenant ID) and run a debounced
 * availability check, mirroring CreateStoreDialog.
 */

import { useState, useEffect, useCallback, useRef } from "react"
import { Loader2, Check, X as XIcon } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  checkSubdomain,
  useUpdateStorefront,
  type StorefrontEntry,
} from "@/hooks/useStoreList"

interface EditStoreDialogProps {
  store: StorefrontEntry | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DOMAIN_SUFFIX = ".shop.zaviago.com"

export function EditStoreDialog({ store, open, onOpenChange }: EditStoreDialogProps) {
  const [label, setLabel] = useState("")
  const [subdomain, setSubdomain] = useState("")
  const [availability, setAvailability] = useState<{ available: boolean; reason: string | null } | null>(null)
  const [checking, setChecking] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updateMutation = useUpdateStorefront()
  const isPrimary = Boolean(store?.is_primary)

  // Seed form from the store being edited whenever the dialog opens
  useEffect(() => {
    if (open && store) {
      setLabel(store.label)
      setSubdomain(store.subdomain)
      setAvailability(null)
      setChecking(false)
    }
  }, [open, store])

  const subdomainChanged = Boolean(store) && subdomain !== store?.subdomain
  const labelChanged = Boolean(store) && label.trim().length > 0 && label.trim() !== store?.label

  // Debounced availability check — only when the subdomain actually changed
  const checkAvailability = useCallback((value: string, changed: boolean) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setAvailability(null)

    if (!changed || !value || value.length < 3) {
      setChecking(false)
      return
    }

    setChecking(true)
    debounceRef.current = setTimeout(async () => {
      const result = await checkSubdomain(value)
      setAvailability(result)
      setChecking(false)
    }, 500)
  }, [])

  useEffect(() => {
    checkAvailability(subdomain, subdomainChanged)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [subdomain, subdomainChanged, checkAvailability])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!store || !canSubmit) return

    updateMutation.mutate(
      {
        storefront_key: store.storefront_key,
        ...(labelChanged ? { label: label.trim() } : {}),
        ...(subdomainChanged ? { subdomain } : {}),
      },
      { onSuccess: () => onOpenChange(false) }
    )
  }

  const canSubmit =
    Boolean(store) &&
    (labelChanged || subdomainChanged) &&
    label.trim().length > 0 &&
    (!subdomainChanged || (subdomain.length >= 3 && availability?.available === true)) &&
    !updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Store</DialogTitle>
          <DialogDescription>
            Update the store name{isPrimary ? "" : " or subdomain"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-6 pb-6 pt-2 space-y-4">
          {/* Store Name */}
          <div className="space-y-1.5">
            <label htmlFor="edit-store-name" className="text-sm font-medium">
              Store Name
            </label>
            <Input
              id="edit-store-name"
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={100}
            />
          </div>

          {/* Subdomain */}
          <div className="space-y-1.5">
            <label htmlFor="edit-store-subdomain" className="text-sm font-medium">
              Subdomain
            </label>
            <div className="relative">
              <Input
                id="edit-store-subdomain"
                value={subdomain}
                disabled={isPrimary}
                onChange={(e) =>
                  setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
                }
                maxLength={63}
                className="pr-8"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checking && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                {!checking && subdomainChanged && availability?.available && (
                  <Check className="w-4 h-4 text-emerald-500" />
                )}
                {!checking && subdomainChanged && availability && !availability.available && (
                  <XIcon className="w-4 h-4 text-red-500" />
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {isPrimary
                ? "The primary store's subdomain cannot be changed"
                : `${subdomain || "your-store"}${DOMAIN_SUFFIX}`}
            </p>
            {!checking && subdomainChanged && availability && !availability.available && (
              <p className="text-xs text-red-500">{availability.reason}</p>
            )}
            {subdomainChanged && (
              <p className="text-xs text-amber-600 dark:text-amber-500">
                Changing the subdomain moves your store to a new address — the
                old link stops working immediately.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={!canSubmit}>
              {updateMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
